import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
	Level,
	LevelCombo,
	LevelComboList,
	TonWallet,
} from '@/core/interfaces';

interface CounterState {
	counter: number;
	tapCounter: number;
	diamonds: number;
	last_time_incremented: number;
	new_diamonds_collected: number;
	friends_reward: number;
	increment_by_second: number;
	increment_by_hour: number;
	total_coins_generated: number;
	earn_per_tap: number;
	max_energy: number;
	current_energy: number;
	energy_tick: number;
	level: number;
	levelName: string;
	levelImage: string;
	levelProgress: number;
	levelsRequeriments: Level[];
	levelCombo: LevelCombo;
	levelComboList: LevelComboList[];
	visiblePendingCoinsDrawer: boolean;
	visibleLevelUpDrawer: boolean;
	newFriendsReward: number;
	tonWallet: TonWallet;
}

const checkAndUpdateLevel = (state: CounterState) => {
	const currentLevel = state.level;
	const nextLevel = state.levelsRequeriments.find(
		(level) => level.level === currentLevel + 1
	);
	if (nextLevel && state.counter >= nextLevel.requiredPoints) {
		state.level = nextLevel.level;
		state.levelName = nextLevel.name;
		state.levelImage = nextLevel.image;
		state.visibleLevelUpDrawer = true;
		state.earn_per_tap += 1;
		state.diamonds +=
			state.levelsRequeriments[nextLevel.level - 1].diamondsReward;
		state.levelCombo.coinsReward =
			state.levelsRequeriments[nextLevel.level - 1].combo.coinsReward;
		state.levelCombo.diamondsReward =
			state.levelsRequeriments[nextLevel.level - 1].combo.diamondsReward;
		state.levelCombo.diamondsCostReveal =
			state.levelsRequeriments[
				nextLevel.level - 1
			].combo.diamondsCostReveal;
	}
	const previousLevel = state.levelsRequeriments.find(
		(level) => level.level === currentLevel
	);
	const pointsForCurrentLevel = previousLevel
		? previousLevel.requiredPoints
		: 0;
	const pointsForNextLevel = nextLevel
		? nextLevel.requiredPoints
		: pointsForCurrentLevel;

	state.levelProgress = Math.trunc(
		(state.counter / pointsForNextLevel) * 100
	);
};

const counterSlice = createSlice({
	name: 'counter',
	initialState: {
		counter: 0,
		tapCounter: 0,
		diamonds: 0,
		last_time_incremented: 0,
		new_diamonds_collected: 0,
		friends_reward: 0,
		increment_by_hour: 0,
		increment_by_second: 0,
		total_coins_generated: 0,
		earn_per_tap: 0,
		max_energy: 0,
		current_energy: 0,
		energy_tick: 0,
		level: 0,
		levelName: '',
		levelImage: '',
		levelProgress: 0,
		levelsRequeriments: [],
		levelCombo: {
			coinsReward: 0,
			diamondsReward: 0,
			diamondsCostReveal: 0,
		},
		levelComboList: [],
		rewards: [],
		visiblePendingCoinsDrawer: true,
		visibleLevelUpDrawer: false,
		newFriendsReward: 0,
		tonWallet: {
			account: '',
			provider: '',
		},
	} as CounterState,
	reducers: {
		setCounter: (state, action: PayloadAction<number>) => {
			checkAndUpdateLevel(state);
			state.counter = action.payload;
		},
		setDiamonds: (state, action: PayloadAction<number>) => {
			state.diamonds = action.payload;
		},
		setLevelCombo: (state, action: PayloadAction<LevelCombo>) => {
			state.levelCombo = action.payload;
		},
		setLevelComboList: (state, action: PayloadAction<LevelComboList[]>) => {
			state.levelComboList = action.payload;
		},
		setEnergyTick: (state, action: PayloadAction<number>) => {
			state.energy_tick = action.payload;
		},
		setMaxEnergy: (state, action: PayloadAction<number>) => {
			state.max_energy = action.payload;
		},
		setNewFriendsReward: (state, action: PayloadAction<number>) => {
			state.newFriendsReward = action.payload;
		},
		setNewDiamondsCollected: (state, action: PayloadAction<number>) => {
			state.new_diamonds_collected = action.payload;
		},
		setCurrentEnergy: (state, action: PayloadAction<number>) => {
			state.current_energy = action.payload;
		},
		setLastTimeIncrement: (state, action: PayloadAction<number>) => {
			state.last_time_incremented = action.payload;
		},
		setFriendsReward: (state, action: PayloadAction<number>) => {
			state.friends_reward = action.payload;
		},
		setIncrementBySecond: (state, action: PayloadAction<number>) => {
			state.increment_by_second = action.payload;
		},
		setIncrementByHour: (state, action: PayloadAction<number>) => {
			state.increment_by_hour = action.payload;
		},
		setTotalCoinsGenerated: (state, action: PayloadAction<number>) => {
			state.total_coins_generated = action.payload;
		},
		setTonWallet: (state, action: PayloadAction<TonWallet>) => {
			state.tonWallet = action.payload;
		},
		setVisiblePendingCoinsDrawer: (
			state,
			action: PayloadAction<boolean>
		) => {
			state.visiblePendingCoinsDrawer = action.payload;
		},
		setVisibleLevelUpDrawer: (state, action: PayloadAction<boolean>) => {
			state.visibleLevelUpDrawer = action.payload;
		},
		setLevel: (state, action: PayloadAction<number>) => {
			state.level = action.payload;
			checkAndUpdateLevel(state);

			const levelRequirement = state.levelsRequeriments.find(
				(req) => req.level === action.payload
			);
			if (levelRequirement) {
				state.levelName = levelRequirement.name;
				state.levelImage = levelRequirement.image;
			} else {
				state.levelName = '';
			}
		},
		setEarnPerTap: (state, action: PayloadAction<number>) => {
			state.earn_per_tap = action.payload;
		},
		increment: (state, action: PayloadAction<number>) => {
			state.counter += action.payload;
			checkAndUpdateLevel(state);
		},
		incrementTapCounter: (state) => {
			state.tapCounter += 1;
		},
		resetTapCounter: (state) => {
			state.tapCounter = 0;
		},
		incrementPerTap: (state) => {
			if (state.current_energy > state.earn_per_tap) {
				state.counter += state.earn_per_tap;
				state.current_energy -= state.earn_per_tap;
				checkAndUpdateLevel(state);
			}
		},
		incrementEnergy: (state, action: PayloadAction<number>) => {
			if (state.max_energy > state.current_energy + state.energy_tick) {
				state.current_energy += action.payload;
			} else {
				state.current_energy = state.max_energy;
			}
		},
		setLevelRequeriments: (state, action: PayloadAction<Level[]>) => {
			state.levelsRequeriments = action.payload;
		},
	},
});

const { reducer, actions } = counterSlice;

export const {
	setCounter,
	setDiamonds,
	setEnergyTick,
	setMaxEnergy,
	setCurrentEnergy,
	setEarnPerTap,
	setLastTimeIncrement,
	setIncrementBySecond,
	setIncrementByHour,
	setTotalCoinsGenerated,
	setNewFriendsReward,
	setLevel,
	increment,
	incrementEnergy,
	incrementPerTap,
	incrementTapCounter,
	resetTapCounter,
	setNewDiamondsCollected,
	setLevelRequeriments,
	setVisiblePendingCoinsDrawer,
	setVisibleLevelUpDrawer,
	setLevelCombo,
	setLevelComboList,
	setTonWallet,

	setFriendsReward,
} = actions;

export default reducer;
